import { computed, defineAsyncComponent, shallowRef } from 'vue';
import type { Router } from 'vue-router';

import { accountService } from '@exchange/libs/account/service/src';
import { ONBOARDING_VERIFICATION_STATUS_URL } from '@exchange/libs/account/service/src/lib/constants.config';
import marketTypeTradingEligibilityService from '@exchange/libs/account/service/src/lib/market-type-trading-eligibility.service';
import { usePromiseWithCB, displayDefaultFailedToast } from '@exchange/libs/composables/shared/src/lib/usePromise';
import { modalService, modalVariant, type ModalVariant } from '@exchange/libs/modals/src';
import PublicRest from '@exchange/libs/rest-api/public-api';
import { toastManagerInstance, SimpleToast } from '@exchange/libs/toasts/src';
import { subaccountsService, SubaccountActionEmitEvent, type SubaccountLimits } from '@exchange/libs/trading-accounts/service/src';
import type { SubaccountCreateType } from '@exchange/libs/trading-accounts/service/src/lib/models/subaccount-model';
import { logger } from '@exchange/libs/utils/simple-logger/src';

const componentMap = {
  subaccountCreate: defineAsyncComponent(() => import('./modals/SubaccountCreate.vue')),
  subaccountLimitReached: defineAsyncComponent(() => import('./modals/SubaccountActionLimitReached.vue')),
  subaccountCreateNotVerified: defineAsyncComponent(() => import('./modals/SubaccountCreateNotVerified.vue')),
  subaccountCreatedSuccess: defineAsyncComponent(() => import('./modals/SubaccountCreatedSuccessModal.vue')),
};

const createSubaccountNotVerified = (variant: ModalVariant = modalVariant.dark) =>
  new Promise<void>((resolve) => {
    const modalKey = modalService.show(
      shallowRef(componentMap.subaccountCreateNotVerified),
      {
        variant,
        verifyLink: ONBOARDING_VERIFICATION_STATUS_URL,
      },
      {
        [SubaccountActionEmitEvent.confirmModal]: () => {
          modalService.hide(modalKey);
          return resolve();
        },
        [SubaccountActionEmitEvent.cancelModal]: () => {
          modalService.hide(modalKey);
          return resolve();
        },
      },
      { variant },
    );
  });

const createSubaccountLimitReached = (variant: ModalVariant = modalVariant.dark) =>
  new Promise<void>((resolve) => {
    const modalKey = modalService.show(
      shallowRef(componentMap.subaccountLimitReached),
      {
        variant,
      },
      {
        [SubaccountActionEmitEvent.cancelModal]: () => {
          modalService.hide(modalKey);
          return resolve();
        },
      },
      { variant },
    );
  });

const showCreationErrorToast = (error) => {
  const displayNotUniqueNameToast = () => {
    toastManagerInstance.addToast({
      content: SimpleToast,
      props: {
        variant: 'warning',
        title: 'fundamentals.warning',
        message: 'modules.subaccount.create.toast.SUBACCOUNT_NAME_NOT_UNIQUE',
      },
    });
  };

  if (error?.uiError) {
    switch (error.uiError) {
      case 'SUBACCOUNT_NAME_NOT_UNIQUE':
        displayNotUniqueNameToast();
        break;
      case 'INVALID_SUBACCOUNT_NAME':
      default:
        displayDefaultFailedToast();
        break;
    }
  } else {
    displayDefaultFailedToast();
  }
};

const actualCreateSubaccount = (name: string, type: SubaccountCreateType) =>
  PublicRest.Subaccounts.create({
    name,
    type,
  });

const triggerSuccessModal = (variant: ModalVariant = modalVariant.dark, router?: Router) =>
  new Promise<void>((resolve) => {
    const navigateToTransfer = () => {
      if (router) {
        router.push({
          ...router.currentRoute.value,
          hash: '#Transfer',
        });
      }
    };

    const modalKey = modalService.show(
      shallowRef(componentMap.subaccountCreatedSuccess),
      { variant },
      {
        'action-clicked': () => {
          modalService.hide(modalKey);
          navigateToTransfer();
          resolve();
        },
        'close-modal': () => {
          modalService.clear();
          resolve();
        },
      },
      { variant },
    );
  });

type CreateOptions = {
  switchToNewAccount?: boolean;
  showSuccessModal?: boolean;
  defaultAccountType?: SubaccountCreateType;
};

/**
 * @returns whether sub-account list should be re-fetched
 */
const handleCreation = (variant: ModalVariant = modalVariant.dark, options: CreateOptions = {}, router?: Router) =>
  new Promise<boolean>((resolve) => {
    const { loading, createPromise: actualCreateSubaccountNow } = usePromiseWithCB(actualCreateSubaccount, false, (e) => {
      throw (e as Dictionary<unknown>).data;
    });

    const modalKey = modalService.show(
      shallowRef(componentMap.subaccountCreate),
      {
        variant,
        processing: loading,
        subaccounts: subaccountsService.fullList.value,
        defaultAccountType: options.defaultAccountType,
      },
      {
        [SubaccountActionEmitEvent.cancelModal]: () => {
          modalService.hide(modalKey);
          return resolve(false);
        },
        [SubaccountActionEmitEvent.confirmModal]: async (formData: { name: string; type: SubaccountCreateType }) => {
          try {
            await actualCreateSubaccountNow(formData.name, formData.type);
            modalService.hide(modalKey);

            if (options.switchToNewAccount) {
              // Fetch updated list to get the new account
              await subaccountsService.fetchList();

              // Find the newly created account by name
              const newAccount = subaccountsService.list.value.find((account) => account.name === formData.name);

              if (newAccount) {
                await subaccountsService.switchTo(newAccount.accountId);
              } else {
                logger.error('Could not find newly created subaccount');
              }
            }

            if (options.showSuccessModal) {
              await triggerSuccessModal(variant, router);
            }

            return resolve(true);
          } catch (error) {
            if ((error as { message?: string })?.message !== 'confirmation-code-was-closed') {
              logger.error('Sub-account creation failed', error);
              showCreationErrorToast(error);
              modalService.hide(modalKey);
              return resolve(false);
            }
            return resolve(false);
          }
        },
      },
      { variant },
    );
  });

const subaccountCreateRow = async ({
  maxActiveLimit,
  getUserIsVerified,
  switchToNewAccount = false,
  showSuccessModal = false,
  defaultAccountType,
  router,
}: {
  maxActiveLimit: number;
  getUserIsVerified: () => boolean;
  switchToNewAccount?: boolean;
  showSuccessModal?: boolean;
  defaultAccountType?: SubaccountCreateType;
  router?: Router;
}) => {
  if (!getUserIsVerified()) {
    await createSubaccountNotVerified();
    return false;
  }

  if (subaccountsService.list.value.length >= maxActiveLimit) {
    await createSubaccountLimitReached();
    return false;
  }

  const refetchList = await handleCreation(
    modalVariant.dark,
    {
      switchToNewAccount,
      showSuccessModal,
      defaultAccountType,
    },
    router,
  );

  return refetchList;
};

export const getSubaccountCreateInfo = () => {
  const subaccountCreateDisabled = computed(() => !accountService.userIsVerified.value);

  const { loading: subaccountCreationIsBeingPrepared, createPromise: subaccountCreatePrepare } = usePromiseWithCB<[undefined, SubaccountLimits]>(
    () => Promise.all([accountService.fetchHolderUser(), subaccountsService.fetchLimits()]),
    true,
  );

  const subaccountCreate = async (options: CreateOptions = {}, router?: Router) => {
    const [, limits] = await subaccountCreatePrepare();
    const refetchList = await subaccountCreateRow({
      maxActiveLimit: limits.maxActive,
      getUserIsVerified: () => accountService.userIsVerified.value,
      switchToNewAccount: options.switchToNewAccount,
      showSuccessModal: options.showSuccessModal,
      defaultAccountType: options.defaultAccountType,
      router,
    });

    if (refetchList) {
      return subaccountsService.fetchList();
    }

    return Promise.resolve();
  };

  const subaccountCreateLoading = computed(() => subaccountCreationIsBeingPrepared.value || marketTypeTradingEligibilityService.isLoadingMarketTypeTradingEligibility.value);

  return {
    subaccountCreate,
    subaccountCreateDisabled,
    subaccountCreateLoading,
  };
};
